import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {ArrowLeft, Search} from 'lucide-react'
import { SearchBar } from '../components/SearchBar'

const allContent = [
  {
    id: 'tom-jerry-magic-ring',
    title: 'Tom & Jerry Magic Ring',
    description: 'The hilarious chaos of Tom and Jerry Magic with the ultimate ring chase adventure.',
    image: 'https://static.lumi.new/67/678dc25a595a0cdc8d11f06c2bbd13e0.png',
    keywords: ['tom', 'jerry', 'magic', 'ring', 'cat', 'mouse', 'chase'],
    path: '/review/tom-jerry-magic-ring'
  },
  {
    id: 'smurfs-magic-flute',
    title: 'The Smurfs Magic Flute',
    description: 'A timeless retro classic featuring the Smurfs and their magical musical adventure.',
    image: 'https://static.lumi.new/87/8717cfaa1cc47f76a788c90416e49295.png',
    keywords: ['smurfs', 'magic', 'flute', 'blue', 'vintage', 'classic'],
    path: '/review/smurfs-magic-flute'
  },
  {
    id: 'morphle-cartoon',
    title: 'Morphle Cartoon',
    description: 'The magical red shape-shifting pet with endless transformations for kids.',
    image: 'https://static.lumi.new/d8/d859fdb4778979e3124cacdf655830a3.png',
    keywords: ['morphle', 'cartoon', 'kids', 'shape', 'shift', 'transform', 'red'],
    path: '/review/morphle-cartoon'
  },
  {
    id: 'magical-maestro',
    title: 'Magical Maestro',
    description: 'A golden age animation masterpiece featuring theatrical magic and musical mayhem.',
    image: 'https://static.lumi.new/5c/5c4ca435178b44ad325564f132b0826e.png',
    keywords: ['magical', 'maestro', 'vintage', 'musical', 'theatrical', 'classic'],
    path: '/'
  },
  {
    id: 'sinbad-magic-belt',
    title: 'Sinbad and His Magic Belt',
    description: 'An Arabian Nights adventure filled with mystical artifacts and daring quests.',
    image: 'https://static.lumi.new/38/38c45e0c24502fee403f9873841cacc6.png',
    keywords: ['sinbad', 'magic', 'belt', 'arabian', 'adventure', 'treasure'],
    path: '/'
  }
]

export function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get('q') || ''

  const handleSearch = (newQuery: string) => {
    setSearchParams({ q: newQuery })
  }

  const results = query
    ? allContent.filter(item => {
        const searchTerms = query.toLowerCase().split(' ')
        return searchTerms.some(term => 
          item.title.toLowerCase().includes(term) ||
          item.description.toLowerCase().includes(term) ||
          item.keywords.some(keyword => keyword.includes(term))
        )
      })
    : allContent

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-pink-500 hover:text-pink-600 mb-8 font-semibold">
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>

        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-8 text-center">
            Search Magical Cartoons
          </h1>
          <SearchBar onSearch={handleSearch} />
        </div>

        {query && (
          <div className="mb-8">
            <p className="text-xl text-gray-700">
              {results.length > 0 
                ? `Found ${results.length} result${results.length !== 1 ? 's' : ''} for "${query}"`
                : `No results found for "${query}"`
              }
            </p>
          </div>
        )}

        {results.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {results.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className="bg-white rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <img 
                  src={item.image}
                  alt={item.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{item.title}</h3>
                  <p className="text-gray-600 mb-4">{item.description}</p>
                  <span className="text-pink-500 font-semibold hover:text-pink-600">
                    Read More →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : query ? (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-600">
              Try searching for "Tom and Jerry", "Smurfs", "Morphle", or other magical cartoons
            </p>
          </div>
        ) : null}
      </div>
    </div>
  )
}
